import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Divider,
  Stack,
  Paper,
  Alert,
} from '@mui/material';
import { PrivateUserInfoType, BankProfile } from '@otw/models';
import { Vehicle } from '@otw/models/inventory';
import { useClientUsers } from 'hooks/useUsers';
import { useBankProfiles } from '@/hooks/admin/useAdminBankProfiles';

export interface SignerInfo {
  first_name: string;
  last_name: string;
  email: string;
  company?: string;
  street_address?: string;
  city?: string;
  state?: string;
  postal_code?: string;
}

export interface VehicleBankProfileAssignment {
  vehicleId: string;
  bankProfile: BankProfile | null;
}

interface InvoiceDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (client: PrivateUserInfoType | SignerInfo, bankProfileAssignments?: VehicleBankProfileAssignment[]) => void;
  loading?: boolean;
  vehicles?: Vehicle[];
}

const InvoiceDialog: React.FC<InvoiceDialogProps> = ({
  open,
  onClose,
  onSubmit,
  loading = false,
  vehicles = [],
}) => {
  const [selectedClient, setSelectedClient] = useState<PrivateUserInfoType | null>(null);
  const [manualFirstName, setManualFirstName] = useState<string>('');
  const [manualLastName, setManualLastName] = useState<string>('');
  const [manualEmail, setManualEmail] = useState<string>('');
  const [manualCompany, setManualCompany] = useState<string>('');
  const [manualStreetAddress, setManualStreetAddress] = useState<string>('');
  const [manualCity, setManualCity] = useState<string>('');
  const [manualState, setManualState] = useState<string>('');
  const [manualZipCode, setManualZipCode] = useState<string>('');
  const [bankProfileAssignments, setBankProfileAssignments] = useState<Record<string, BankProfile | null>>({});
  const { clientUsers, isLoading: isLoadingClientUsers } = useClientUsers();
  const { bankProfiles, isLoading: isLoadingBankProfiles } = useBankProfiles();
  const inventoryBankProfiles = useMemo(
    () => (bankProfiles || []).filter(profile => profile.profileType === 'inventory'),
    [bankProfiles]
  );

  // Filter vehicles with liens
  const vehiclesWithLiens = useMemo(() => {
    return vehicles.filter(vehicle => vehicle.has_lien === true);
  }, [vehicles]);

  // Get unique who values from all vehicles
  const uniqueWhoValues = useMemo(() => {
    const whoValues = vehicles
      .map(vehicle => vehicle.who)
      .filter((who): who is string => who !== null && who !== undefined && who.trim() !== '');
    
    // Get unique values
    const unique = [...new Set(whoValues)];
    return unique;
  }, [vehicles]);

  // Format who values for display in title
  // If all vehicles have the same who value, display it once
  // If vehicles have different who values, display all of them comma-separated
  const whoDisplayText = useMemo(() => {
    if (uniqueWhoValues.length === 0) {
      return '';
    } else if (uniqueWhoValues.length === 1) {
      return uniqueWhoValues[0];
    } else {
      // Display all unique values separated by commas
      return uniqueWhoValues.join(', ');
    }
  }, [uniqueWhoValues]);

  useEffect(() => {
    if (!open) {
      setSelectedClient(null);
      setManualFirstName('');
      setManualLastName('');
      setManualEmail('');
      setManualCompany('');
      setManualStreetAddress('');
      setManualCity('');
      setManualState('');
      setManualZipCode('');
      setBankProfileAssignments({});
    }
  }, [open]);

  // Initialize bank profile assignments when vehicles with liens change
  useEffect(() => {
    if (open && vehiclesWithLiens.length > 0) {
      const initialAssignments: Record<string, BankProfile | null> = {};
      vehiclesWithLiens.forEach(vehicle => {
        initialAssignments[vehicle.id] = bankProfileAssignments[vehicle.id] || null;
      });
      setBankProfileAssignments(prev => ({
        ...prev,
        ...initialAssignments,
      }));
    }
  }, [open, vehiclesWithLiens]);

  const handleSubmit = () => {
    const assignments: VehicleBankProfileAssignment[] = vehiclesWithLiens.map(vehicle => ({
      vehicleId: vehicle.id,
      bankProfile: bankProfileAssignments[vehicle.id] || null,
    }));
    const signerInfo: SignerInfo = {
      first_name: manualFirstName,
      last_name: manualLastName,
      email: manualEmail,
      company: manualCompany.trim() || undefined,
      street_address: manualStreetAddress.trim() || undefined,
      city: manualCity.trim() || undefined,
      state: manualState.trim() || undefined,
      postal_code: manualZipCode.trim() || undefined,
    };
    onSubmit(signerInfo, assignments.length > 0 ? assignments : undefined);
  };

  const hasClientInfo =
    manualFirstName.trim() !== '' &&
    manualLastName.trim() !== '' &&
    manualEmail.trim() !== '' &&
    manualCompany.trim() !== '' &&
    manualStreetAddress.trim() !== '' &&
    manualCity.trim() !== '' &&
    manualState.trim() !== '' &&
    manualZipCode.trim() !== '';
  
  // Check if all vehicles with liens have a bank profile selected
  const allVehiclesWithLiensHaveBank = vehiclesWithLiens.length === 0 || 
    vehiclesWithLiens.every(vehicle => bankProfileAssignments[vehicle.id] !== null && bankProfileAssignments[vehicle.id] !== undefined);
  
  const canSubmit = hasClientInfo && allVehiclesWithLiensHaveBank;

  // Format date for display
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch {
      return dateString;
    }
  };

  // Format currency
  const formatCurrency = (amount: number | null | undefined) => {
    if (amount === null || amount === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Generate Purchase Order{whoDisplayText ? ` for ${whoDisplayText}` : ''}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* Display vehicles with liens */}
          {vehiclesWithLiens.length > 0 && (
            <>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Vehicles with Liens ({vehiclesWithLiens.length})
                </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Select a bank profile for each vehicle. A payoff authorization will be generated for these vehicles. All vehicles with liens must have a bank profile selected.
                </Typography>
                <Stack spacing={2}>
                  {vehiclesWithLiens.map((vehicle) => (
                    <Paper key={vehicle.id} variant="outlined" sx={{ p: 1.5, backgroundColor: 'background.default' }}>
                      <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
                        {vehicle.stock_number} - {vehicle.year} {vehicle.make} {vehicle.model}
                      </Typography>
                      <Stack spacing={0.5} sx={{ mb: 1.5 }}>
                        {vehicle.lien_lender && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Lender:</strong> {vehicle.lien_lender}
                          </Typography>
                        )}
                        {vehicle.lien_payoff_amount !== null && vehicle.lien_payoff_amount !== undefined && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Payoff Amount:</strong> {formatCurrency(vehicle.lien_payoff_amount)}
                          </Typography>
                        )}
                        {vehicle.lien_good_through_date && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Good Through:</strong> {formatDate(vehicle.lien_good_through_date)}
          </Typography>
                        )}
                      </Stack>
            <Autocomplete<BankProfile, false, false, false>
              options={inventoryBankProfiles}
              getOptionLabel={(option) => option.bankName}
                        value={bankProfileAssignments[vehicle.id] || null}
              onChange={(_, newValue) => {
                          setBankProfileAssignments(prev => ({
                            ...prev,
                            [vehicle.id]: newValue,
                          }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Bank Profile"
                            placeholder="Select a bank profile"
                            size="small"
                  required
                            error={!bankProfileAssignments[vehicle.id]}
                            helperText={!bankProfileAssignments[vehicle.id] ? "Required for vehicles with liens" : ""}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoadingBankProfiles && <CircularProgress size={20} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {option.bankName}
                    </Typography>
                    {option.email && (
                      <Typography variant="caption" color="text.secondary">
                        {option.email}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              loading={isLoadingBankProfiles}
              noOptionsText={
                isLoadingBankProfiles 
                  ? 'Loading bank profiles...' 
                  : 'No inventory bank profiles found'
              }
              filterOptions={(options, { inputValue }) => {
                if (typeof inputValue === 'string' && inputValue.trim() === '') {
                  return options;
                }
                return options.filter((option) => {
                  const bankName = option.bankName.toLowerCase();
                  const searchTerm = inputValue.toLowerCase();
                  return (
                    bankName.includes(searchTerm) ||
                    option.email?.toLowerCase().includes(searchTerm)
                  );
                });
              }}
            />
                    </Paper>
                  ))}
                </Stack>
              </Alert>
              <Divider sx={{ my: 2 }} />
            </>
          )}
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Client
          </Typography>
          <Autocomplete<PrivateUserInfoType, false, false, false>
            options={clientUsers || []}
            getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
            value={selectedClient}
            onChange={(_, newValue) => {
              setSelectedClient(newValue);
              if (newValue) {
                setManualFirstName(newValue.first_name ?? '');
                setManualLastName(newValue.last_name ?? '');
                setManualEmail(newValue.email ?? '');
                setManualCompany(newValue.company_name ?? '');
                setManualStreetAddress(newValue.address?.street ?? '');
                setManualCity(newValue.address?.city ?? '');
                setManualState(newValue.address?.state ?? '');
                setManualZipCode(newValue.address?.zipCode ?? '');
              } else {
                setManualFirstName('');
                setManualLastName('');
                setManualEmail('');
                setManualCompany('');
                setManualStreetAddress('');
                setManualCity('');
                setManualState('');
                setManualZipCode('');
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select client (optional)"
                placeholder="Search by name, email, or company"
                helperText={selectedClient ? "Fields below are filled from this client. You can edit them." : "Select a client to fill the fields below, or enter details manually."}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoadingClientUsers && <CircularProgress size={20} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    {option.first_name} {option.last_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {option.email}
                  </Typography>
                  {option.company_name && (
                    <Typography variant="caption" color="text.secondary">
                      Company: {option.company_name}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={isLoadingClientUsers}
            noOptionsText={
              isLoadingClientUsers ? 'Loading clients...' : 'No clients found'
            }
            filterOptions={(options, { inputValue }) => {
              if (typeof inputValue === 'string' && inputValue.trim() === '') {
                return options;
              }
              const searchTerm = inputValue.toLowerCase();
              return options.filter((option) => {
                const fullName = `${option.first_name} ${option.last_name}`.toLowerCase();
                const email = option.email?.toLowerCase() || '';
                const companyName = option.company_name?.toLowerCase() || '';
                return fullName.includes(searchTerm) || email.includes(searchTerm) || companyName.includes(searchTerm);
              });
            }}
          />
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
            Client details
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {selectedClient ? 'Pre-filled from selected client. Edit as needed.' : 'Enter client details manually.'}
          </Typography>
          <Stack spacing={2} sx={{ mb: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="First Name"
                value={manualFirstName}
                onChange={(e) => setManualFirstName(e.target.value)}
                fullWidth
                required
              />
              <TextField
                label="Last Name"
                value={manualLastName}
                onChange={(e) => setManualLastName(e.target.value)}
                fullWidth
                required
              />
            </Stack>
            <TextField
              label="Email"
              type="email"
              value={manualEmail}
              onChange={(e) => setManualEmail(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Company Name"
              value={manualCompany}
              onChange={(e) => setManualCompany(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Street Address"
              value={manualStreetAddress}
              onChange={(e) => setManualStreetAddress(e.target.value)}
              fullWidth
              required
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="City"
                value={manualCity}
                onChange={(e) => setManualCity(e.target.value)}
                fullWidth
                required
              />
              <TextField
                label="State"
                value={manualState}
                onChange={(e) => setManualState(e.target.value)}
                fullWidth
                required
              />
              <TextField
                label="Zip Code"
                value={manualZipCode}
                onChange={(e) => setManualZipCode(e.target.value)}
                fullWidth
                required
              />
            </Stack>
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!canSubmit || loading}
          variant="contained"
        >
          {loading ? 'Generating...' : 'Generate Purchase Order'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceDialog;

